import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Notification } from '../types'

interface NotificationSystemProps {
  showInline?: boolean
  maxVisible?: number
}

// Mock notifications - replace with API calls
const mockNotifications: Notification[] = [
  {
    id: 'notif1',
    userId: 'current-user',
    type: 'mention',
    title: 'You were mentioned',
    message: 'KratomExpert mentioned you in a comment',
    isRead: false,
    dateCreated: new Date('2024-12-08T14:30:00Z'),
    actionUrl: '/community/thread/thread1#comment-123',
    relatedUserId: 'user1',
    relatedUsername: 'KratomExpert'
  },
  {
    id: 'notif2',
    userId: 'current-user',
    type: 'reply',
    title: 'New reply to your post',
    message: 'QualityVendor replied to your post about strain recommendations',
    isRead: false,
    dateCreated: new Date('2024-12-08T13:15:00Z'),
    actionUrl: '/community/thread/thread2#post-456',
    relatedUserId: 'vendor1',
    relatedUsername: 'QualityVendor'
  },
  {
    id: 'notif3',
    userId: 'current-user',
    type: 'follow',
    title: 'New follower',
    message: 'NewMember started following you',
    isRead: true,
    dateCreated: new Date('2024-12-08T12:00:00Z'),
    actionUrl: '/profile/user2',
    relatedUserId: 'user2',
    relatedUsername: 'NewMember'
  },
  {
    id: 'notif4',
    userId: 'current-user',
    type: 'like',
    title: 'Your post was liked',
    message: 'TrustedUser and 3 others liked your post',
    isRead: true,
    dateCreated: new Date('2024-12-08T11:30:00Z'),
    actionUrl: '/community/thread/thread3#post-789'
  },
  {
    id: 'notif5',
    userId: 'current-user',
    type: 'message',
    title: 'New message',
    message: 'You have a new message from StrainExpert',
    isRead: true,
    dateCreated: new Date('2024-12-08T10:45:00Z'),
    actionUrl: '/messages?conversation=conv3',
    relatedUserId: 'user3',
    relatedUsername: 'StrainExpert'
  },
  {
    id: 'notif6',
    userId: 'current-user',
    type: 'system',
    title: 'Welcome to the community!',
    message: 'Your account has been verified. You can now participate in all discussions.',
    isRead: true,
    dateCreated: new Date('2024-12-07T15:00:00Z'),
    actionUrl: '/community'
  }
]

function getNotificationIcon(type: Notification['type']): string {
  switch (type) {
    case 'mention': return '@'
    case 'reply': return '💬'
    case 'like': return '👍'
    case 'follow': return '👤'
    case 'message': return '✉️'
    case 'system': return 'ℹ️'
    default: return '🔔'
  }
}

function getNotificationColor(type: Notification['type']): string {
  switch (type) {
    case 'mention': return '#4299e1'
    case 'reply': return '#00d166'
    case 'like': return '#e53e3e'
    case 'follow': return '#9f7aea'
    case 'message': return '#ed8936'
    case 'system': return '#6b7280'
    default: return '#a0aec0'
  }
}

function formatTimestamp(date: Date): string {
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

export default function NotificationSystem({ showInline = false, maxVisible = 5 }: NotificationSystemProps) {
  const { user, isLoggedIn } = useAuth()
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isLoggedIn) {
      loadNotifications()
    }
  }, [isLoggedIn])

  const loadNotifications = async () => {
    setLoading(true)
    try {
      // TODO: API call to get notifications
      setNotifications(mockNotifications)
    } catch (error) {
      console.error('Error loading notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  const markAsRead = async (notificationId: string) => {
    try {
      // TODO: API call to mark notification as read
      console.log('Marking notification as read:', notificationId)

      setNotifications(prev => prev.map(notif => 
        notif.id === notificationId ? { ...notif, isRead: true } : notif
      ))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const markAllAsRead = async () => {
    try {
      // TODO: API call to mark all notifications as read
      console.log('Marking all notifications as read')

      setNotifications(prev => prev.map(notif => ({ ...notif, isRead: true })))
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const deleteNotification = async (notificationId: string) => {
    try {
      // TODO: API call to delete notification
      console.log('Deleting notification:', notificationId)

      setNotifications(prev => prev.filter(notif => notif.id !== notificationId))
    } catch (error) {
      console.error('Error deleting notification:', error)
    }
  }

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id)
    }
    setShowDropdown(false)
  }

  const unreadCount = notifications.filter(n => !n.isRead).length
  const displayNotifications = showInline ? 
    notifications.slice(0, maxVisible) : 
    notifications

  if (!isLoggedIn) return null

  if (showInline) {
    return (
      <div className="notifications-inline">
        <div className="notifications-header">
          <h3>Recent Notifications</h3>
          {unreadCount > 0 && (
            <button onClick={markAllAsRead} className="mark-all-read">
              Mark all as read
            </button>
          )}
        </div>

        <div className="notifications-list">
          {loading ? (
            <div className="notification-loading">Loading notifications...</div>
          ) : displayNotifications.length === 0 ? (
            <div className="no-notifications">
              <span className="empty-icon">🔔</span>
              <p>No notifications yet</p>
            </div>
          ) : (
            displayNotifications.map(notification => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                onClick={() => handleNotificationClick(notification)}
                onDelete={() => deleteNotification(notification.id)}
                showDelete={true}
              />
            ))
          )}
        </div>

        {notifications.length > maxVisible && (
          <div className="notifications-footer">
            <Link to="/notifications" className="view-all-link">
              View all notifications ({notifications.length})
            </Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="notification-system">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`notification-bell ${unreadCount > 0 ? 'has-unread' : ''}`}
        title="Notifications"
      >
        <span className="bell-icon">🔔</span>
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className="notification-dropdown">
          <div className="dropdown-header">
            <h4>Notifications</h4>
            <div className="dropdown-actions">
              {unreadCount > 0 && (
                <button onClick={markAllAsRead} className="mark-all-read">
                  Mark all read
                </button>
              )}
              <button 
                onClick={() => setShowDropdown(false)}
                className="close-dropdown"
              >
                ×
              </button>
            </div>
          </div>

          <div className="dropdown-content">
            {loading ? (
              <div className="notification-loading">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <span className="empty-icon">🔔</span>
                <p>No notifications</p>
              </div>
            ) : (
              notifications.slice(0, 10).map(notification => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onClick={() => handleNotificationClick(notification)}
                  compact={true}
                />
              ))
            )}
          </div>

          <div className="dropdown-footer">
            <Link 
              to="/notifications" 
              className="view-all-link"
              onClick={() => setShowDropdown(false)}
            >
              View all notifications
            </Link>
          </div>
        </div>
      )}

      {showDropdown && (
        <div 
          className="notification-overlay"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  )
}

// Individual notification item component
function NotificationItem({
  notification,
  onClick,
  onDelete,
  compact = false,
  showDelete = false
}: {
  notification: Notification
  onClick: () => void
  onDelete?: () => void
  compact?: boolean
  showDelete?: boolean
}) {
  const handleClick = () => {
    onClick()
    if (notification.actionUrl) {
      // Navigate to the notification's action URL
      window.location.href = notification.actionUrl
    }
  }

  return (
    <div 
      className={`notification-item ${!notification.isRead ? 'unread' : ''} ${compact ? 'compact' : ''}`}
      onClick={handleClick}
    >
      <div className="notification-content">
        <div 
          className="notification-icon"
          style={{ backgroundColor: getNotificationColor(notification.type) }}
        >
          {getNotificationIcon(notification.type)}
        </div>
        
        <div className="notification-body">
          <div className="notification-title">{notification.title}</div>
          <div className="notification-message">{notification.message}</div>
          <div className="notification-time">
            {formatTimestamp(notification.dateCreated)}
          </div>
        </div>
      </div>

      <div className="notification-actions">
        {!notification.isRead && <div className="unread-indicator" />}
        
        {showDelete && onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
            className="delete-notification"
            title="Delete notification"
          >
            ×
          </button>
        )}
      </div>
    </div>
  )
}

// Hook for managing notifications
export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const { user, isLoggedIn } = useAuth()

  const addNotification = (notification: Omit<Notification, 'id' | 'userId' | 'dateCreated'>) => {
    if (!user) return

    const newNotification: Notification = {
      ...notification,
      id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      userId: user.id,
      dateCreated: new Date()
    }

    setNotifications(prev => [newNotification, ...prev])

    // TODO: Send to server/real-time system
    console.log('New notification:', newNotification)
  }

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => prev.map(notif => 
      notif.id === notificationId ? { ...notif, isRead: true } : notif
    ))
  }

  const getUnreadCount = () => {
    return notifications.filter(n => !n.isRead).length
  }

  return {
    notifications,
    addNotification,
    markAsRead,
    unreadCount: getUnreadCount()
  }
}